import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { CLIPBOARD_SCORE } from "@src/appV2/ClipboardScore/constants";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  modalState: UseModalState;
  shiftBlockId: string;
  facilityId: string;
  totalShifts: number;
  onConfirmCancellation: () => void;
}

export function CancelShiftBlockBottomSheet(props: Props) {
  const { modalState, shiftBlockId, facilityId, totalShifts, onConfirmCancellation } = props;
  const { path: currentRouterUrl } = useRouteMatch();

  const handleKeepClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.KEEP_BLOCK_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts,
      location: currentRouterUrl,
    });
    modalState.closeModal();
  }, [currentRouterUrl, facilityId, modalState, shiftBlockId, totalShifts]);

  const handleCancelClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.YES_CANCEL_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts,
      location: currentRouterUrl,
    });
    modalState.closeModal();
    onConfirmCancellation();
  }, [currentRouterUrl, facilityId, modalState, onConfirmCancellation, shiftBlockId, totalShifts]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="horizontal">
          <Button fullWidth variant="outlined" size="large" onClick={handleKeepClicked}>
            Keep block
          </Button>
          <Button fullWidth variant="destructive" size="large" onClick={handleCancelClicked}>
            Yes, cancel
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <Text bold variant="h4" sx={{ textAlign: "center" }}>
            Cancel This Block
          </Text>
          <Illustration type="cancellation" />
          <Text align="center">
            This cancellation will remove you from all future shifts in this block and penalize your{" "}
            {CLIPBOARD_SCORE}. Are you sure you want to cancel this block?
          </Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
